import React, { FC, useContext } from 'react'
import styled from 'styled-components'

import { Flex } from 'components/Box'
import Button, { AnchorButton } from 'components/Button'
import DownloadFileAnchor from 'components/DownloadFile'
import ErrorMessage from 'components/ErrorMessage'
import Footer from 'components/Footer'
import ModalContext from 'containers/ModalManager/ModalContext'
import { ShareInfoFragment } from 'gqlTypes'
import { ReactComponent as TrashLarge } from 'icons/trash-bin-64.svg'
import PageLayout from 'layouts/PageLayout'
import { RestoreShareModal } from 'modules/shares/components/RestoreShareModal'
import { getDownloadProps } from 'modules/shares/utils'
import Navbar from 'components/Navbar'
import { IsSignedIn } from 'containers/IsSignedIn'
import HeaderWorkspaceBadge from '../components/HeaderWorkspaceBadge'
import UserNavbarSection from 'modules/user/components/UserNavbarSection'

export type ShareForDeletedView = Pick<
  ShareInfoFragment,
  'name' | 'identifier' | 'version' | 'workspace'
>

export interface ShareDeletedViewProps {
  share: ShareForDeletedView
}

const Layout = styled(PageLayout)`
  justify-content: center;
  align-items: center;
`

const DownloadButton = styled(DownloadFileAnchor).attrs({
  as: AnchorButton,
  variant: 'primary',
  intent: 'primary',
})``

export const ShareDeletedView: FC<ShareDeletedViewProps> = ({ share }) => {
  const { name, identifier } = share
  const { showModal } = useContext(ModalContext)

  const title = 'Cannot open document in Trash'
  const download = getDownloadProps(share.version)

  const onRestore = () => {
    showModal(RestoreShareModal, {
      name,
      shareIdentifier: identifier,
    })
  }

  return (
    <Layout
      title={title}
      header={
        <Navbar>
          <IsSignedIn>
            <HeaderWorkspaceBadge workspace={share.workspace!} />
            <Navbar.Section align="end">
              <Navbar.Divider />
              <UserNavbarSection />
            </Navbar.Section>
          </IsSignedIn>
        </Navbar>
      }
      footer={<Footer />}>
      <ErrorMessage
        icon={<TrashLarge />}
        iconSize="medium"
        title={title}
        style={{ flex: 1 }}
        extra={
          <Flex>
            {download?.isAvailable && (
              <DownloadButton href={download.url}>Download</DownloadButton>
            )}
            <Button variant="primary" onClick={onRestore}>
              Restore
            </Button>
          </Flex>
        }>
        You cannot open “{share.name}” because it&apos;s in the Workspace Trash.
      </ErrorMessage>
    </Layout>
  )
}
